// src/components/messaging/MessageInput.tsx
import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Tooltip,
  Typography,
  alpha,
  CircularProgress,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ImageIcon from '@mui/icons-material/Image';
import MicIcon from '@mui/icons-material/Mic';
import CloseIcon from '@mui/icons-material/Close';
import { Message, GroupMessage } from '../../types/messaging';
import { useTranslation } from 'react-i18next';
import AudioRecorder from './AudioRecorder';

interface MessageInputProps {
  onSend: (content: string, replyToId?: number) => Promise<void>;
  onSendFile: (file: File, type: 'image' | 'file' | 'audio', replyToId?: number) => Promise<void>;
  onTyping?: (isTyping: boolean) => void;
  replyTo?: Message | GroupMessage | null;
  onCancelReply?: () => void;
  editMessage?: Message | GroupMessage | null;
  onCancelEdit?: () => void;
  onEditSubmit?: (messageId: number, content: string) => Promise<void>;
  disabled?: boolean;
}

const MessageInput: React.FC<MessageInputProps> = ({
  onSend,
  onSendFile,
  onTyping,
  replyTo,
  onCancelReply,
  editMessage,
  onCancelEdit,
  onEditSubmit,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [content, setContent] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Handle audio send
  const handleSendAudio = useCallback(
    async (audioBlob: Blob) => {
      if (isUploading || disabled) return;

      // Backend accepts: audio/ogg (among others)
      // Send as audio/ogg which is compatible with browser recordings
      const audioFile = new File(
        [audioBlob],
        `voice_message_${Date.now()}.ogg`,
        { type: 'audio/ogg' }
      );

      setIsUploading(true);
      setIsRecording(false);

      try {
        await onSendFile(audioFile, 'audio', replyTo?.id);
        onCancelReply?.();
      } catch (error) {
        console.error('Failed to send audio:', error);
      } finally {
        setIsUploading(false);
      }
    },
    [isUploading, disabled, onSendFile, replyTo?.id, onCancelReply]
  );

  // Cancel recording
  const handleCancelRecording = useCallback(() => {
    setIsRecording(false);
  }, []);

  // Set edit content
  useEffect(() => {
    if (editMessage) {
      setContent(editMessage.content || '');
      inputRef.current?.focus();
    }
  }, [editMessage]);

  // Focus on reply
  useEffect(() => {
    if (replyTo) {
      inputRef.current?.focus();
    }
  }, [replyTo]);

  // Handle typing indicator
  const handleTyping = useCallback(() => {
    if (!onTyping) return;

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Send typing true
    onTyping(true);

    // Set timeout to send typing false
    typingTimeoutRef.current = setTimeout(() => {
      onTyping(false);
    }, 3000);
  }, [onTyping]);

  // Handle input change
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setContent(e.target.value);
    handleTyping();
  };

  // Handle send
  const handleSend = async () => {
    const trimmedContent = content.trim();
    if (!trimmedContent || isSending || disabled) return;

    // Clear typing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      onTyping?.(false);
    }

    setIsSending(true);

    try {
      if (editMessage && onEditSubmit) {
        await onEditSubmit(editMessage.id, trimmedContent);
        onCancelEdit?.();
      } else {
        await onSend(trimmedContent, replyTo?.id);
        onCancelReply?.();
      }
      setContent('');
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setIsSending(false);
      inputRef.current?.focus();
    }
  };

  // Handle key press
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Handle file select
  const handleFileSelect = async (
    e: React.ChangeEvent<HTMLInputElement>,
    type: 'image' | 'file'
  ) => {
    const file = e.target.files?.[0];
    if (!file || isUploading || disabled) return;

    // Reset input
    e.target.value = '';

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert(t('messaging.fileTooLarge', 'Le fichier est trop volumineux (max 10MB)'));
      return;
    }

    setIsUploading(true);

    try {
      await onSendFile(file, type, replyTo?.id);
      onCancelReply?.();
    } catch (error) {
      console.error('Failed to upload file:', error);
    } finally {
      setIsUploading(false);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Box
      sx={{
        p: 2,
        borderTop: '1px solid',
        borderColor: 'grey.200',
        backgroundColor: 'background.paper',
      }}
    >
      {/* Reply preview */}
      {replyTo && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 1,
            mb: 1,
            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
            borderLeft: '3px solid',
            borderLeftColor: 'primary.main',
            borderRadius: 1,
          }}
        >
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>
              {t('messaging.replyingTo', 'Réponse à')}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {replyTo.content || (replyTo.type === 'image' ? 'Image' : 'Fichier')}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onCancelReply}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      {/* Edit preview */}
      {editMessage && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 1,
            mb: 1,
            backgroundColor: (theme) => alpha(theme.palette.warning.main, 0.08),
            borderLeft: '3px solid',
            borderLeftColor: 'warning.main',
            borderRadius: 1,
          }}
        >
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            <Typography variant="caption" color="warning.main" sx={{ fontWeight: 600 }}>
              {t('messaging.editing', 'Modification')}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onCancelEdit}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      {/* Input area */}
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
        {/* Show AudioRecorder when recording */}
        {isRecording ? (
          <AudioRecorder
            onSend={handleSendAudio}
            onCancel={handleCancelRecording}
            maxDuration={120}
          />
        ) : (
          <>
            {/* Attachment buttons */}
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => handleFileSelect(e, 'image')}
              />
              <input
                ref={fileInputRef}
                type="file"
                accept="*/*"
                style={{ display: 'none' }}
                onChange={(e) => handleFileSelect(e, 'file')}
              />
              <Tooltip title={t('messaging.attachImage', 'Joindre une image')}>
                <IconButton
                  size="small"
                  onClick={() => imageInputRef.current?.click()}
                  disabled={disabled || isUploading || !!editMessage}
                  sx={{
                    backgroundColor: (theme) => alpha(theme.palette.grey[500], 0.08),
                    '&:hover': {
                      backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                    },
                  }}
                >
                  {isUploading ? <CircularProgress size={18} /> : <ImageIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Tooltip title={t('messaging.attachFile', 'Joindre un fichier')}>
                <IconButton
                  size="small"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={disabled || isUploading || !!editMessage}
                  sx={{
                    backgroundColor: (theme) => alpha(theme.palette.grey[500], 0.08),
                    '&:hover': {
                      backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                    },
                  }}
                >
                  <AttachFileIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {/* Mic button for voice recording */}
              <Tooltip title={t('messaging.recordVoice', 'Enregistrer un message vocal')}>
                <IconButton
                  size="small"
                  onClick={() => setIsRecording(true)}
                  disabled={disabled || isUploading || !!editMessage}
                  sx={{
                    backgroundColor: (theme) => alpha(theme.palette.grey[500], 0.08),
                    '&:hover': {
                      backgroundColor: (theme) => alpha(theme.palette.error.main, 0.1),
                      color: 'error.main',
                    },
                  }}
                >
                  <MicIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Text input */}
            <TextField
              inputRef={inputRef}
              fullWidth
              multiline
              maxRows={4}
              placeholder={t('messaging.typePlaceholder', 'Écrivez un message...')}
              value={content}
              onChange={handleChange}
              onKeyPress={handleKeyPress}
              disabled={disabled || isSending}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  backgroundColor: (theme) => alpha(theme.palette.grey[500], 0.04),
                  '& fieldset': {
                    borderColor: 'grey.200',
                  },
                  '&:hover fieldset': {
                    borderColor: 'grey.300',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: 1,
                  },
                },
                '& .MuiInputBase-input': {
                  py: 1.25,
                  px: 1.5,
                },
              }}
            />

            {/* Send button */}
            <Tooltip title={editMessage ? t('messaging.save', 'Enregistrer') : t('messaging.send', 'Envoyer')}>
              <span>
                <IconButton
                  color="primary"
                  onClick={handleSend}
                  disabled={!content.trim() || disabled || isSending}
                  sx={{
                    backgroundColor: 'primary.main',
                    color: 'white',
                    width: 40,
                    height: 40,
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: 'grey.200',
                      color: 'grey.400',
                    },
                  }}
                >
                  {isSending ? <CircularProgress size={20} color="inherit" /> : <SendIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}
      </Box>
    </Box>
  );
};

export default MessageInput;
